/**
 * O texto da premissa que a IA citou na polaridade (2026-10-21).
 *
 * A menção guarda só o NÚMERO GLOBAL da premissa (`polarity_rule_position`:
 * o "Regra: 49" que a IA devolve), e a tela das justificativas dizia
 * "IA · premissa nº 49" sem dizer o que a regra afirma. As premissas moram
 * no cadastro (`GET /prompt-rules`); este módulo as cruza com a menção.
 *
 * - `premiseIndex` é o cruzamento, PURO: chave `(marca, fonte, posição
 *   GLOBAL)` → rótulo da seção e texto. O número que a tela mostra é sempre
 *   o global (o que a IA cita e a menção grava, decisão da dona): um número
 *   único por premissa é mais fácil de achar que "nº 4 de Desfavorável".
 * - `usePremiseTexts` busca UMA vez por empresa e entrega o `premiseFor`.
 *   Falha na busca não aparece na tela: `premiseFor` devolve `null` e a
 *   linha fica como antes (selo com o número global, sem texto).
 *
 * Premissa inativa ou apagada ENTRA: a menção pode ter sido classificada
 * quando ela valia. Entre duas com a mesma chave, a ativa vence.
 */

import { useEffect, useMemo, useState } from "react";

import { apiClient, type PromptRule } from "@/shared/api/api-client";

/** A tarefa cujas premissas a polaridade cita. */
export const POLARITY_RULE_TASK = "polarity_intensity";

/** Onde começa a faixa de cada `rule_type` de polaridade e como a tela o
 *  chama. Espelha `RULE_TYPE_RANGE_START` de `core/vocabulary.py` (os quatro
 *  de polaridade; os de papel repetem 1/16/31 em outra tarefa e não entram
 *  aqui). A paridade é travada em `use-premise-texts.test.ts`. */
export const POLARITY_RULE_RANGES: Record<string, { start: number; label: string }> = {
  POSITIVO: { start: 1, label: "Positivo" },
  NEGATIVO: { start: 16, label: "Negativo" },
  FAVORAVEL: { start: 31, label: "Favorável" },
  DESFAVORAVEL: { start: 46, label: "Desfavorável" }
};

/** A largura de cada faixa (`MAX_RULES_PER_TYPE` do core). */
const RANGE_WIDTH = 15;

export type Premise = {
  /** "Positivo", "Favorável"… */
  sectionLabel: string;
  text: string;
};

export type PremiseFor = (brandId: number, source: string, position: number) => Premise | null;

const keyOf = (brandId: number, source: string, position: number) =>
  `${brandId}|${source}|${position}`;

/** O `premiseFor` de uma lista de premissas. Pura. */
export function premiseIndex(rules: readonly PromptRule[]): PremiseFor {
  const index = new Map<string, { premise: Premise; active: boolean }>();
  for (const rule of rules) {
    const range = POLARITY_RULE_RANGES[rule.rule_type];
    if (!range) continue;
    // Posição fora da faixa do próprio tipo é lixo: o motor a descartaria.
    const offset = rule.position - range.start;
    if (offset < 0 || offset >= RANGE_WIDTH) continue;
    const key = keyOf(rule.brand_id, rule.source, rule.position);
    const active = rule.is_active && rule.deleted_on === null;
    const existing = index.get(key);
    if (existing?.active && !active) continue;
    index.set(key, { premise: { sectionLabel: range.label, text: rule.text }, active });
  }
  return (brandId, source, position) => index.get(keyOf(brandId, source, position))?.premise ?? null;
}

const NO_PREMISE: PremiseFor = () => null;

/**
 * Busca as premissas de polaridade da empresa uma vez (por `token` e
 * `companyId`) e devolve o `premiseFor`. `enabled=false` não busca nada —
 * o "Mais informações" só precisa disso quando a polaridade é da IA.
 */
export function usePremiseTexts(token: string, companyId: string, enabled = true): PremiseFor {
  const [rules, setRules] = useState<PromptRule[] | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let current = true;
    apiClient
      .listPromptRules(token, { task: POLARITY_RULE_TASK, includeInactive: true })
      .then(result => {
        if (current) setRules(result);
      })
      .catch(() => {
        // O texto da premissa é complemento: sem ele a linha fica como era.
        if (current) setRules(null);
      });
    return () => {
      current = false;
    };
  }, [token, companyId, enabled]);

  return useMemo(() => (rules ? premiseIndex(rules) : NO_PREMISE), [rules]);
}
